/* Beardbrand – Bundle component availability controller (v9)
 *
 * Uses variant metafield JSON (from snippets/variant-metafield-data.liquid)
 * to drive a unified "unavailable" state for bundle variants:
 *
 *   - baseAvailable:   product.variants[n].available (Shopify stock)
 *   - componentsAvailable: metafield-driven bundle_og_component_ids
 *
 * A variant is considered AVAILABLE only if:
 *   baseAvailable === true AND componentsAvailable === true
 *
 * Effects:
 *   - On first load ONLY: if the initial variant is not available,
 *     auto-switch to the first bundle-available variant (if any).
 *   - Disables ATC pill + sets "Unavailable" text when not available.
 *   - Adds sold-out styling to ALL currently-selected swatches
 *     (every checked radio in every option group).
 *   - Dispatches bb:variant:unavailable for BOTH stock- and
 *     component-driven OOS states (for Back-in-Stock widgets).
 */

(function () {
  if (window.__BB_BUNDLE_AVAIL_V9__) return;
  window.__BB_BUNDLE_AVAIL_V9__ = true;

  // ---------- Helpers ----------

  var metaCache = new WeakMap();
  var parsedProductJson = null;

  function getProductJson() {
    if (parsedProductJson) return parsedProductJson;
    var script =
      document.querySelector('script[type="application/json"][id="product-json"]') ||
      document.querySelector('script[type="application/json"][data-product-json]');
    if (!script) return null;
    try {
      parsedProductJson = JSON.parse(script.textContent || script.innerText || '{}');
    } catch (e) {
      console.warn('[BB Bundle Availability] Failed to parse #product-json', e);
      parsedProductJson = null;
    }
    return parsedProductJson;
  }

  // Build a map: variantId -> metafield data, and mirror component
  // availability into product JSON so theme.js can see it.
  function getVariantMetaMap(root) {
    if (!root) return {};
    if (metaCache.has(root)) return metaCache.get(root);

    var map = {};

    try {
      var scripts = root.querySelectorAll('script[data-variant-metafields]');
      if (!scripts.length) {
        scripts = document.querySelectorAll('script[data-variant-metafields]');
      }

      scripts.forEach(function (el) {
        try {
          var json = el.textContent || el.innerText || '{}';
          var parsed = JSON.parse(json);
          if (parsed && typeof parsed === 'object') {
            Object.assign(map, parsed);
          }
        } catch (e) {
          console.warn('[BB Bundle Availability] Failed to parse variant-metafields JSON', e);
        }
      });
    } catch (e) {
      console.warn('[BB Bundle Availability] Error reading variant-metafields scripts', e);
    }

    try {
      var product = getProductJson();
      if (product && Array.isArray(product.variants)) {
        Object.keys(map).forEach(function (vid) {
          var entry = map[vid];
          if (entry && entry.components_available === false) {
            product.variants.forEach(function (v) {
              if (String(v.id) === String(vid)) {
                v.available = false;
              }
            });
          }
        });
        window.BB_BUNDLE_PRODUCT_JSON = product;
      }
    } catch (e) {
      console.warn(
        '[BB Bundle Availability] Failed to mirror bundle availability into product JSON',
        e
      );
    }

    metaCache.set(root, map);
    return map;
  }

  function isBundleVariantsEl(el) {
    return !!(el && el.dataset && el.dataset.bundleProduct === 'true');
  }

  function getCurrentVariantId(root) {
    if (!root || !root.querySelector) return null;

    var idInput =
      root.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      root.querySelector('form[action*="/cart/add"] select[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);

    try {
      var url = new URL(window.location.href);
      var v = url.searchParams.get('variant');
      if (v) return String(v);
    } catch (e) {}

    return null;
  }

  function clearBundleSoldOutStyling(root) {
    Array.prototype.forEach.call(
      root.querySelectorAll('.bb-bundle-oos-label'),
      function (lbl) {
        lbl.classList.remove('bb-bundle-oos-label', 'product-variant-value--unavailable');
      }
    );

    Array.prototype.forEach.call(
      root.querySelectorAll('.product-variant__item input.product-variant__input'),
      function (input) {
        input.classList.remove('sold-out-swatch');
        input.removeAttribute('aria-label');
      }
    );
  }

  // Find the first variant that is truly "bundle-available":
  // baseAvailable && componentsAvailable.
  function findFirstBundleAvailableVariant(metaMap) {
    var product = getProductJson();
    if (!product || !Array.isArray(product.variants)) return null;

    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var vidStr = String(v.id);
      var meta = metaMap && metaMap[vidStr] ? metaMap[vidStr] : {};
      var baseAvailable = !!v.available;
      var componentsAvailable = meta.components_available !== false;
      if (baseAvailable && componentsAvailable) {
        return v;
      }
    }
    return null;
  }

  // Force-select a given variant by toggling its option inputs.
  // This is called ONLY during initial auto-select, before we wire events.
  function selectVariantByOptions(root, variantsEl, variant) {
    if (!root || !variantsEl || !variant) return;

    var type = variantsEl.dataset.type || 'radio';
    var options = [variant.option1, variant.option2, variant.option3];

    options.forEach(function (value, index) {
      if (!value) return;

      var groups = variantsEl.querySelectorAll('.product-variant');
      var group = groups && groups[index] ? groups[index] : null;
      if (!group) return;

      if (type === 'select') {
        var select = group.querySelector('select');
        if (select) {
          select.value = value;
          // fire change so Shopify's core JS updates the hidden id, media, etc.
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        var esc = value;
        if (window.CSS && window.CSS.escape) {
          esc = window.CSS.escape(value);
        } else {
          esc = value.replace(/"/g, '\\"');
        }

        var input =
          group.querySelector('.product-variant__input[value="' + esc + '"]') ||
          group.querySelector('[value="' + esc + '"]');

        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  // Runs ONCE per product root, before we wire listeners.
  function maybeAutoSelectInitialVariant(root, variantsEl, metaMap) {
    var currentId = getCurrentVariantId(root);
    if (!currentId) return;

    var product = getProductJson();
    if (!product || !Array.isArray(product.variants)) return;

    var currentVariant = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(currentId)) {
        currentVariant = product.variants[i];
        break;
      }
    }
    if (!currentVariant) return;

    var meta = metaMap[String(currentVariant.id)] || {};
    var baseAvailable = !!currentVariant.available;
    var componentsAvailable = meta.components_available !== false;
    var currentIsAvailable = baseAvailable && componentsAvailable;

    if (currentIsAvailable) return; // nothing to do

    var fallbackVariant = findFirstBundleAvailableVariant(metaMap);
    if (!fallbackVariant || String(fallbackVariant.id) === String(currentVariant.id)) {
      // Either no available bundle variant, or it's the same one; leave as-is.
      return;
    }

    // Auto-select fallback combo once; Shopify's own handlers will update the id.
    selectVariantByOptions(root, variantsEl, fallbackVariant);
  }

  // ---------- Core logic per product root ----------

  function wireBundleRoot(root) {
    if (!root || root.__bbBundleAvailWired) return;
    root.__bbBundleAvailWired = true;

    var variantsEl = root.querySelector('product-variants[data-bundle-product="true"]');
    if (!variantsEl || !isBundleVariantsEl(variantsEl)) return;

    var metaMap = getVariantMetaMap(root);

    // ONE-TIME: ensure we start on a truly available bundle variant if possible.
    maybeAutoSelectInitialVariant(root, variantsEl, metaMap);

    function applyState() {
      var vid = getCurrentVariantId(root);
      if (!vid) return;

      var product = getProductJson();
      var productVariant = null;
      var baseAvailable = true;

      if (product && Array.isArray(product.variants)) {
        for (var i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(vid)) {
            productVariant = product.variants[i];
            baseAvailable = !!productVariant.available;
            break;
          }
        }
      }

      var meta = metaMap[String(vid)] || {};
      var componentsAvailable = meta.components_available !== false;

      // Variant is purchasable only if BOTH base + components are available
      var isAvailable = baseAvailable && componentsAvailable;

      var form = root.querySelector('form[action*="/cart/add"]');
      var btn =
        (form &&
          (form.querySelector('[type="submit"]') ||
            form.querySelector('.product__add-to-cart') ||
            form.querySelector('.product-form__cart-submit'))) ||
        root.querySelector('.product__add-to-cart, .product-form__cart-submit');

      clearBundleSoldOutStyling(root);

      if (isAvailable) {
        root.removeAttribute('data-bb-bundle-oos');

        if (btn) {
          var original = btn.getAttribute('data-bb-original-text');
          if (original) btn.textContent = original;

          btn.classList.remove('disabled');
          btn.removeAttribute('disabled');
          btn.setAttribute('aria-disabled', 'false');
        }

        root.dispatchEvent(
          new CustomEvent('bb:bundle:components-available', {
            bubbles: true,
            detail: {
              variantId: vid,
              baseAvailable: baseAvailable,
              componentsAvailable: componentsAvailable
            }
          })
        );

        return;
      }

      // === Unavailable (stock OR components OR both) ===
      root.setAttribute('data-bb-bundle-oos', 'true');

      if (btn) {
        if (!btn.hasAttribute('data-bb-original-text')) {
          btn.setAttribute('data-bb-original-text', (btn.textContent || '').trim());
        }

        var unavailableText =
          btn.getAttribute('data-bb-unavailable-text') || 'Unavailable';

        btn.textContent = unavailableText;
        btn.classList.add('disabled');
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
      }

      // Apply sold-out styling to ALL currently-selected radios
      var checkedInputs = root.querySelectorAll(
        '.product-variant__input[type="radio"]:checked'
      );

      checkedInputs.forEach(function (input) {
        if (!input.id) return;

        input.classList.add('sold-out-swatch');
        input.setAttribute(
          'aria-label',
          'This variant is currently unavailable for purchase'
        );

        var label = root.querySelector('label[for="' + input.id + '"]');
        if (label) {
          label.classList.add(
            'bb-bundle-oos-label',
            'product-variant-value--unavailable'
          );
        }
      });

      var select = root.querySelector('.product-variants, select[name="id"]');
      if (select) {
        select.classList.add('bb-bundle-oos-select');
      }

      // Fire unified "unavailable" events for Back-in-Stock & badges
      var detail = {
        variantId: vid,
        baseAvailable: baseAvailable,
        componentsAvailable: componentsAvailable
      };

      root.dispatchEvent(
        new CustomEvent('bb:bundle:components-unavailable', {
          bubbles: true,
          detail: detail
        })
      );
      root.dispatchEvent(
        new CustomEvent('bb:variant:unavailable', {
          bubbles: true,
          detail: detail
        })
      );
    }

    // Initial run (after possible one-time auto-select)
    applyState();

    // Re-run when variants change (user interaction / other scripts)
    var eventsToWatch = ['change', 'VARIANT_CHANGE', 'variant:change', 'bb:variant:change'];
    eventsToWatch.forEach(function (evtName) {
      root.addEventListener(evtName, function () {
        applyState();
      });
    });
  }

  function boot() {
    var roots = document.querySelectorAll(
      'product-page, .product-page, .product--section, .shopify-section'
    );
    if (!roots.length) roots = [document.body];

    roots.forEach(function (root) {
      wireBundleRoot(root);
    });

    var mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes || [], function (node) {
          if (!(node instanceof HTMLElement)) return;
          if (
            (node.matches &&
              node.matches(
                'product-page, .product-page, .product--section, .shopify-section'
              )) ||
            node.querySelector('product-variants[data-bundle-product="true"]')
          ) {
            wireBundleRoot(node);
          }
        });
      });
    });

    mo.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
